import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
  DiscardGuard,
} from "@/components/ui/dialog";
import { Callout } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input, Textarea } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { useAddNumbers } from "@/lib/queries";
import { splitNumbers } from "@/lib/types";

/**
 * Add numbers to the pool — superadmin only.
 *
 * A textarea rather than a row-per-number form: numbers arrive from the carrier as a block,
 * and pasting one is how they get here. The server normalises every line to E.164 and refuses
 * the whole batch if one is already in the pool, naming it, so nothing lands half-way.
 */
export function AddNumbersDialog({
  open, onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const add = useAddNumbers();
  const [raw, setRaw] = React.useState("");
  const [numberType, setNumberType] = React.useState("local");
  const [region, setRegion] = React.useState("");
  const [locality, setLocality] = React.useState("");
  const [notes, setNotes] = React.useState("");
  const [formError, setFormError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setRaw("");
    setNumberType("local");
    setRegion("");
    setLocality("");
    setNotes("");
    setFormError(null);
  }, [open]);

  const numbers = splitNumbers(raw);
  const changed =
    raw.trim() !== "" || numberType !== "local" || region.trim() !== "" ||
    locality.trim() !== "" || notes.trim() !== "";

  const submit = async () => {
    setFormError(null);
    try {
      const created = await add.mutateAsync(
        numbers.map((number) => ({
          number,
          number_type: numberType,
          region: region.trim() || null,
          locality: locality.trim() || null,
          notes: notes.trim() || null,
        })),
      );
      toast.success(
        `${created.length} number${created.length === 1 ? "" : "s"} added`,
        created.map((n) => n.number).slice(0, 3).join(", ") + (created.length > 3 ? ", …" : ""),
      );
      onOpenChange(false);
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not add the numbers.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer">
        <DiscardGuard changed={changed} />
        <DialogHeader>
          <DialogTitle>Add numbers</DialogTitle>
          <DialogDescription>
            Numbers the platform holds at the carrier. They join the pool as available, for
            tenants to request.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          {formError && <Callout tone="danger">{formError}</Callout>}

          <Field
            label="Numbers"
            htmlFor="add-numbers"
            required
            hint={
              numbers.length
                ? `${numbers.length} number${numbers.length === 1 ? "" : "s"} — stored as E.164, e.g. +13105550142.`
                : "One per line. US numbers may be typed any way: (310) 555-0142, 3105550142, +13105550142."
            }
          >
            <Textarea
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
              placeholder={"+13105550142\n+13105550143"}
              rows={6}
              spellCheck={false}
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Type" htmlFor="add-type">
              <Select value={numberType} onValueChange={setNumberType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="local">Local</SelectItem>
                  <SelectItem value="tollfree">Toll-free</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="State" htmlFor="add-region" hint="Applies to every line.">
              <Input value={region} onChange={(e) => setRegion(e.target.value)} placeholder="CA" autoComplete="off" />
            </Field>
            <Field label="City" htmlFor="add-locality">
              <Input value={locality} onChange={(e) => setLocality(e.target.value)} placeholder="Los Angeles" autoComplete="off" />
            </Field>
          </div>

          <Field label="Notes" htmlFor="add-notes" hint="Where they came from, for the next person.">
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Twilio block, Sept 2026" autoComplete="off" />
          </Field>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="default"
            loading={add.isPending}
            disabled={numbers.length === 0}
            onClick={() => void submit()}
          >
            Add {numbers.length || ""} number{numbers.length === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
